<template>
    <div 
        class="wallpaper"
        :class="wallpaperContent.background_color"
    >
        <ProgressLoadingBar v-show="showLoader" />

        <div 
            v-if="pageContent[pageID].pagemeta.enable_transparent_layer"
            class="wallpaper-overlay">
        </div>

        <div
            class="container"
            :class="wallpaperContent.iframe && 'h-dvh'"
        >
            <FadeInTransition>
                <div v-if="wallpaperContent.wallpaper[0]?.path && !wallpaperContent.iframe" class="wallpaper-background-image">
                    <ImageWidget 
                        :source="wallpaperContent.wallpaper"
                        :no-spacing="true"
                        class="h-full"
                        :classes="['h-full', 'w-full', 'object-cover']"
                        @loaded="showLoader = false"
                    ></ImageWidget>
                </div>
            </FadeInTransition>

            <FadeInTransition>
                <div 
                    v-if="wallpaperContent.iframe"
                    class="wallpaper-background-image"
                    :class="!wallpaperContent.longDescription && 'wallpaper-action-background'"
                >
                    <iframe
                        ref="wallpaperIframe"
                        :src="wallpaperContent.iframe"
                        :onload="iframeLoaded"
                        loading="lazy"
                        id="wallpaper-iframe"
                    ></iframe>
                </div>
            </FadeInTransition>

            <div class="wallpaper-inner spacing-alternative flex-default">
                <FadeInTransition>
                    <div v-if="!wallpaperContent.iframe && wallpaperContent.flow_image[0]?.path" class="wallpaper-image-container">
                        <ImageWidget 
                            :source="wallpaperContent.flow_image"
                            :no-spacing="true"
                            class="wallpaper-image"
                            @loaded="showLoader = false"
                        ></ImageWidget>
                    </div>
                </FadeInTransition>

                <FadeInTransition>
                    <div 
                        v-if="wallpaperContent.longDescription"
                        class="wallpaper-text-container hyphens-manual"
                        :class="{
                            'offset-1': !wallpaperContent.flow_image[0]?.path,
                            'smaller-headline': smallerHeadline,
                            'overlay-text': pageContent[pageID].pagemeta.enable_transparent_layer
                        }"
                        v-html="wallpaperContent.longDescription"
                    >
                    </div>
                </FadeInTransition>
            </div>
        </div>

        <SlideUpTransition>
            <CtaShowMore :action="scrollDown" label="Scroll down" />
        </SlideUpTransition>

        <SlideUpTransition>
            <div
                v-if="pageContent[pageID].pagemeta.ai_tag === 1"
                class="ai-tag-container spacing-alternative"
            >
                <FadeInTransition>
                    <div
                        v-show="showAIText"
                        class="ai-tag-description"
                    >
                        <span>{{ globals?.pagemeta?.ai_tag_text }}</span>
                    </div>
                </FadeInTransition>
    
                <span 
                    class="ai-tag"
                    @mouseover="showAIText = true" 
                    @mouseleave="showAIText = false"
                >AI</span>
            </div>
        </SlideUpTransition>
    </div>
</template>

<script setup lang="ts">
    import { CookieHandler } from "@/composables/Classes/CookieHandler"

    const { $iframeMessenger } = useNuxtApp()

    const requestStore = useRequestStore()
    const { globals, pageContent, pageID, darkmode } = storeToRefs(requestStore)

    const cookie = new CookieHandler(requestStore)

    const route = useRoute();

    const showAIText = ref<boolean>(false)
    const showLoader = ref<boolean>(true)
    const wallpaperIframe = ref<HTMLIFrameElement | null>(null)
    const actionIframe = ref<any>(null)
    const smallerHeadline = ref<boolean>(false)

    const wallpaperContent = computed(() => {
        const content = pageContent.value[pageID.value].pagecontent

        const h1StartIndex = content.longDescription.indexOf('<h1>')
        const h1EndIndex = content.longDescription.indexOf('</h1>')
        const h1String = content.longDescription.substring(h1StartIndex + 4, h1EndIndex)

        if (h1String?.length >= 50) {
            smallerHeadline.value = true
        }

        return content
    })

    const scrollDown = () => {
        const wallpaper: any = document.querySelector(".wallpaper");

        window.scrollTo({
            top: wallpaper.offsetHeight,
            behavior: 'smooth'
        });
    }

    const iframeLoaded = () => {
        showLoader.value = false
        
        let functionalCookiesAccepted
        const stageExperience = wallpaperIframe.value?.contentWindow

        if (cookie.getCookie("functionalCookies")) {
            functionalCookiesAccepted = true
            setTimeout(() => {
                $iframeMessenger.post('wallpaper-iframe', 'cookies', { functionalCookies: true })
            }, 400)
        } else {
            functionalCookiesAccepted = false
            setTimeout(() => {
                $iframeMessenger.post('wallpaper-iframe', 'cookies', { functionalCookies: false })
            }, 400)
        }

        if (darkmode.value) {
            setTimeout(() => {
                $iframeMessenger.post('wallpaper-iframe', 'darkmode', true)
            }, 50)
        } else {
            setTimeout(() => {
                $iframeMessenger.post('wallpaper-iframe', 'darkmode', false)
            }, 50)
        }

        if (localStorage.getItem('visitorName')) {
            setTimeout(() => {
                $iframeMessenger.post('wallpaper-iframe', 'name', localStorage.getItem('visitorName'))
            }, 50)
        }
    }

    const iframeLoadHandler = () => {
        const iframe = document.getElementById("wallpaper-iframe")

        iframe?.addEventListener("load", () => {
            console.log("GOT CONTENT")
        })
    }

    
    const initialize = () => {
        iframeLoadHandler()
    }

    onMounted(() => {
        initialize()

        $iframeMessenger.on('checkDarkmode', (payload: boolean) => {
            if (payload) {
                $iframeMessenger.post('wallpaper-iframe', 'darkmode', darkmode.value)
            }
        })

        $iframeMessenger.on('scrollDown', (payload: boolean) => {
            if (payload) {
                scrollDown()
            }
        })
    })
</script>
